/**
 * Short label for a level, in the vocabulary the structure rail already uses
 * ("E9·5m", "S50·1m", "SWL·5m" — see the rail rows on the chart).
 *
 * Why this exists: the model's level-hold markers (MV_HOLD_S/R) rendered as a
 * circle reading "AI", which names the speaker rather than the claim. Every
 * marker that reaches the chart is already gated to p >= 0.90 AND
 * tape-informed, so confidence is near-constant and carries no information —
 * what varies, and what the reader wants, is WHICH level held.
 *
 * The level's timeframe is its own, NOT the chart's: a 5m EMA is the same
 * price whether you are looking at a 1m or a 15m pane, so the label reads the
 * same on every timeframe. That is deliberate.
 *
 * Input is the level's `type` as the server sends it (vision frame `levels[].type`,
 * carried onto the signal as `metrics.mv.ind`) — 38 distinct values observed live:
 *   {TF}_{IND}   1m_EMA9, 5m_SMA21, 15m_EMA200, 5m_SwingLow, 1H_SwingLow, 1W_EMA21
 *   standalone   VWAP, SessionHigh, PriorDayLow, PriorWeekHigh
 * plus `undefined` on families that carry no type (flip, retained), which fall
 * back to the family name.
 */

/** EMA21 -> E21, SMA50 -> S50, SwingLow -> SWL, VWAP -> VWAP. */
function abbrIndicator(ind: string): string {
  const ma = /^(EMA|SMA)([0-9]+)$/i.exec(ind);
  if (ma) return (ma[1]!.toUpperCase() === 'EMA' ? 'E' : 'S') + ma[2];
  switch (ind.toLowerCase()) {
    case 'swinghigh': return 'SWH';
    case 'swinglow': return 'SWL';
    case 'vwap': return 'VWAP';
    case 'sessionhigh': return 'HOD';
    case 'sessionlow': return 'LOD';
    case 'priordayhigh': return 'PDH';
    case 'priordaylow': return 'PDL';
    case 'priorweekhigh': return 'PWH';
    case 'priorweeklow': return 'PWL';
    default: return ind;
  }
}

/** '1m' | '5m' | '15m' | '1H' | '4H' | '1W' — the level's own timeframe. */
const TF_RE = /^(1m|3m|5m|15m|30m|1H|4H|1D|1W)$/i;

/**
 * @param type   the level's `type` (may be undefined on typeless families)
 * @param family the level's `family`, used only as a fallback
 * @returns a short label, or '' when neither is usable (caller keeps its default)
 */
export function levelLabel(type?: string | null, family?: string | null): string {
  const t = String(type ?? '').trim();
  if (t && t !== 'undefined') {
    const cut = t.indexOf('_');
    if (cut > 0) {
      const tf = t.slice(0, cut);
      const ind = t.slice(cut + 1);
      if (TF_RE.test(tf) && ind) return `${abbrIndicator(ind)}·${tf}`;
    }
    return abbrIndicator(t);
  }
  // Typeless families (flip, retained, prior, session) still name themselves.
  const f = String(family ?? '').trim();
  if (!f || f === 'undefined') return '';
  // 'ma-5m' -> 'MA·5m', 'swing-15m' -> 'SWING·15m', 'vwap' -> 'VWAP'
  const dash = f.lastIndexOf('-');
  if (dash > 0 && TF_RE.test(f.slice(dash + 1))) {
    return `${f.slice(0, dash).toUpperCase()}·${f.slice(dash + 1)}`;
  }
  return f.toUpperCase();
}

/**
 * Merge labels that land on the SAME bar and side. Same label repeats as "×N"
 * (the existing convention); genuinely different levels join with "+", capped
 * so a violent bar cannot grow an unbounded chip.
 */
export function mergeLabels(labels: readonly string[]): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0]!;
  const uniq = [...new Set(labels)];
  if (uniq.length === 1) return `${uniq[0]} ×${labels.length}`;
  if (uniq.length === 2) return uniq.join('+');
  return `${uniq[0]}+${uniq.length - 1}`;
}
